import SquigglyLine11Icon from '@/assets/icons/squiggly-lines/squiggly-line-11.svg?react';
import SquigglyLine4Icon from '@/assets/icons/squiggly-lines/squiggly-line-4.svg?react';
import { Heading } from '@/components/common/Heading';
import { Section } from '@/components/common/Section';
import { cn } from '@/lib/cn';
import { useBreakpoint } from '@/lib/contexts/BreakpointContext';
import {
  DocumentSentiment as DocumentSentimentIcon,
  Medication as MedicationIcon,
  Phone as PhoneIcon,
  Renew as RenewIcon,
} from '@carbon/icons-react';

const benefits = [
  {
    title: 'For you',
    features: [
      {
        Icon: MedicationIcon,
        title: 'Supplements that fit you',
        description:
          'Your protocol evolves with your body — we adjust it with every retest or consultation.',
      },
      {
        Icon: RenewIcon,
        title: 'Delivered monthly, hassle-free',
        description:
          'We send your supplements on time — no reordering, no stress.',
      },
      {
        Icon: DocumentSentimentIcon,
        title: 'Feel supported, always',
        description:
          'Questions? Concerns? Our team is always here for you, ready to help',
      },
      {
        Icon: PhoneIcon,
        title: 'Clarity in every capsule',
        description:
          'Know what you’re taking, why it matters, and how it helps you — no mystery pills.',
      },
    ],
  },
  {
    title: 'For your doctors',
    features: [
      {
        title: 'Doctor-designed, not algorithm-made',
        description:
          'Each supplement protocol is created or approved by your doctor, based on your real health needs.',
      },
      {
        title: 'Simple, seamless prescribing',
        description:
          'Our system makes it easy to adjust and approve supplement plans with just a few clicks.',
      },
      {
        title: 'Direct access to our team',
        description:
          'Doctors get a dedicated line to our pharmacists and support team for fast help.',
      },
      {
        title: 'Full transparency',
        description:
          'Every ingredient, dosage, and rationale is clearly outlined for review.',
      },
    ],
  },
];

export function BenefitsSection() {
  const { isDesktop } = useBreakpoint();

  return (
    <Section containerClassName="lg:pb-19">
      <Heading
        as="h2"
        className="mb-18 lg:mb-19"
        underlinedText={isDesktop ? 'Benefits' : 'patients'}
        underline={
          isDesktop ? (
            <SquigglyLine11Icon className="-bottom-2 -left-0.5" />
          ) : (
            <SquigglyLine4Icon className="-bottom-2 -left-1" />
          )
        }
      >
        Benefits for doctors and the patients
      </Heading>

      <div className="flex flex-col gap-10.5 lg:mx-auto lg:flex-row lg:items-start lg:justify-center lg:gap-5">
        {benefits.map(({ title, features }) => (
          <div key={title} className="lg:w-full lg:max-w-111.25">
            <Heading
              as="h3"
              size={isDesktop ? 'h4' : 'h3'}
              className="bg-secondary text-secondary-foreground mb-2 w-fit rounded-[0.625rem] px-5 py-2.5 text-lg leading-6.25 lg:mb-4.25"
            >
              {title}
            </Heading>

            <div
              className={cn(
                'flex flex-col',
                features.some((feature) => 'Icon' in feature) || 'gap-2.75',
              )}
            >
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className={cn(
                    'border-b border-[#EAE5D5] py-8.5 last-of-type:border-none lg:pt-9 lg:pr-5 lg:pb-8.75',
                    'Icon' in feature ||
                      (isDesktop &&
                        'bg-muted rounded-[0.75rem] lg:px-5 lg:pt-9 lg:pb-7.5'),
                  )}
                >
                  <div className="mb-2.5 flex items-center gap-2.5">
                    {'Icon' in feature && <feature.Icon className="size-8" />}
                    <Heading
                      as="h4"
                      size={isDesktop ? 'h5' : 'h4'}
                      className="h-4 text-lg leading-4 lg:h-auto lg:leading-6"
                    >
                      {feature.title}
                    </Heading>
                  </div>

                  <p className="text-muted-foreground pr-1.5 leading-6.5 lg:pr-0">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
